import React, { useState, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from './ui/select';
import { 
  Edit2, Plus, Package, Check, X, AlertCircle, Eye, 
  GripVertical, Trash2, Palette, Ruler, Clock, Tag, PenSquare, Image as ImageIcon,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import VariantManagerV2 from './VariantManagerV2';
import { useAuth } from '../context/AuthContext';

// Common variant suggestions based on listing type
const COMMON_VARIANTS = {
  product: ['Size', 'Color', 'Material', 'Style'],
  service: ['Duration', 'Package', 'Level', 'Add-ons'],
  rental: ['Duration', 'Package', 'Size', 'Color'],
  event: ['Ticket Type', 'Seating', 'Package', 'Add-ons']
};

/**
 * VariantEditorModal - Enhanced modal with WYSIWYG style and bulk edit
 * 
 * Features:
 * - Live preview showing how variants appear to customers
 * - Quick add common variant types
 * - Bulk edit prices and SKUs
 * - Visual variant combinations table
 * - Mobile-friendly tabbed view for Editor/Preview
 */
const VariantEditorModal = ({
  formData,
  handleChange,
  schema,
  errors = {},
  cardConfig = {},
  listingId // Add listingId prop to know which listing to update
}) => {
  const { apiCall } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [mobileTab, setMobileTab] = useState('editor'); // 'editor' or 'preview'
  const [isSaving, setIsSaving] = useState(false);
  const variantIdCounter = useRef(0);
  
  const variants = formData.variants || [];
  const variantCombinations = formData.variantCombinations || [];
  const hasVariants = formData.hasVariants;
  
  const listingType = schema?.listingType || 'product';
  const listingTypeLabel = listingType === 'service' ? 'Service' : 
                           listingType === 'rental' ? 'Rental' : 
                           listingType === 'event' ? 'Ticket' : 'Product';
  
  // Calculate summary stats
  const totalVariantOptions = variants.reduce((sum, v) => sum + (v.options?.length || 0), 0);
  const totalCombinations = variantCombinations.length;
  const totalInventory = variantCombinations.reduce((sum, c) => sum + (parseInt(c.stock) || parseInt(c.inventory) || 0), 0);
  
  // Get common variants for this listing type
  const commonVariants = COMMON_VARIANTS[listingType] || COMMON_VARIANTS.product;
  
  // Check for validation errors
  const hasErrors = Object.keys(errors).some(key => 
    key.startsWith('variant') || key.startsWith('combo')
  );

  const handleToggleVariants = (checked) => {
    handleChange('hasVariants', checked);
    if (checked && !isOpen) {
      setIsOpen(true);
    }
  };

  const handleSave = async () => {
    // If we have a listingId and variant combinations, save inventory directly to main document
    if (listingId && variantCombinations.length > 0) {
      setIsSaving(true);
      try {
        const result = await apiCall(`/api/listings/${listingId}/variant-inventory`, {
          method: 'PATCH',
          body: JSON.stringify({ variant_combinations: variantCombinations })
        });
        
        if (result.success) {
          toast.success(`Inventory updated: ${result.total_inventory} total units`);
          if (result.shopify_synced) {
            toast.success('Synced to Shopify');
          }
        }
      } catch (error) {
        console.error('Error saving variant inventory:', error);
        // Don't show error toast - the form will still auto-save to revision_data
        // This just means the direct update failed
      } finally {
        setIsSaving(false);
      }
    }
    
    setIsOpen(false);
    setShowBulkEdit(false);
  };

  // Handle bulk price update
  const handleBulkPriceUpdate = (adjustment, type = 'fixed') => {
    const updatedCombinations = variantCombinations.map(combo => ({
      ...combo,
      price: type === 'fixed' 
        ? adjustment 
        : (parseFloat(combo.price) || 0) + adjustment
    }));
    handleChange('variantCombinations', updatedCombinations);
  };

  // Quick add a variant type
  const handleQuickAddVariant = (variantName) => {
    variantIdCounter.current += 1;
    const newVariant = {
      id: `var_${variantIdCounter.current}_${variantName.toLowerCase().replace(/\s+/g, '_')}`,
      name: variantName,
      displayType: variantName.toLowerCase() === 'color' ? 'color_swatch' : 'button',
      options: []
    };
    handleChange('variants', [...variants, newVariant]);
    if (!isOpen) setIsOpen(true);
  };

  return (
    <>
      {/* Summary Card - Shown in Form */}
      <Card className={`transition-all duration-200 ${hasVariants ? 'border-blue-200' : 'border-dashed'}`}>
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Checkbox
                id="hasVariants"
                checked={hasVariants}
                onCheckedChange={handleToggleVariants}
                data-testid="toggle-variants"
              />
              <div>
                <Label htmlFor="hasVariants" className="cursor-pointer font-medium">
                  {cardConfig.title || `${listingTypeLabel} Variants`}
                </Label>
                {!hasVariants && cardConfig.description && (
                  <p className="text-xs text-gray-500 mt-0.5">{cardConfig.description}</p>
                )}
              </div>
            </div>
            {hasVariants && (
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  {totalCombinations > 0 ? `${totalCombinations} combinations` : 'Enabled'}
                </Badge>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsOpen(true)}
                  data-testid="edit-variants-btn"
                >
                  <Edit2 className="h-3 w-3 mr-1" />
                  Edit
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        
        {/* Empty state with clear CTA */}
        {!hasVariants && (
          <CardContent className="pt-0 pb-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-dashed border-gray-200 text-center">
              <Package className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm text-gray-600 mb-3">No variants added yet</p>
              <p className="text-xs text-gray-400 mb-4">
                Add variants when your {listingType} comes in multiple options
              </p>
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={() => handleToggleVariants(true)}
                className="mb-3"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Variant (e.g., {commonVariants.slice(0, 2).join(', ')})
              </Button>
              <p className="text-xs text-gray-400">
                Common variants: {commonVariants.join(' • ')}
              </p>
            </div>
          </CardContent>
        )}

        {/* Summary when enabled with preview */}
        {hasVariants && (
          <CardContent className="pt-0 pb-4">
            {hasErrors && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                <div className="flex items-center gap-2 text-red-700 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  <span>Some variant fields need attention</span>
                </div>
              </div>
            )}
            
            {totalCombinations > 0 ? (
              <>
                {/* Stats Grid */}
                <div className="grid grid-cols-3 gap-3 text-center mb-4">
                  <div className="bg-gray-50 rounded-lg p-2">
                    <p className="text-lg font-semibold text-gray-900">{variants.length}</p>
                    <p className="text-xs text-gray-500">Variant Types</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    <p className="text-lg font-semibold text-gray-900">{totalCombinations}</p>
                    <p className="text-xs text-gray-500">Combinations</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    <p className="text-lg font-semibold text-gray-900">{totalInventory}</p>
                    <p className="text-xs text-gray-500">Total Stock</p>
                  </div>
                </div>
                
                {/* Mini Preview */}
                <div className="border rounded-lg p-3 bg-white">
                  <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
                    <Eye className="h-3 w-3" />
                    <span>Customer Preview</span>
                  </div>
                  {variants.slice(0, 2).map((variant, idx) => {
                    const displayType = variant.display_config || variant.displayType || 'buttons';
                    return (
                      <div key={variant.id || idx} className="mb-2">
                        <p className="text-xs font-medium text-gray-700 mb-1">{variant.name}</p>
                        <div className="flex flex-wrap gap-1">
                          {variant.options?.slice(0, 5).map((opt, optIdx) => {
                            // Color swatches or image swatches - show circular
                            if (displayType === 'color_swatch' || displayType === 'swatches') {
                              return (
                                <div
                                  key={optIdx}
                                  className={`w-6 h-6 rounded-full border-2 overflow-hidden ${optIdx === 0 ? 'border-blue-500' : 'border-gray-200'}`}
                                  style={displayType === 'color_swatch' ? { backgroundColor: opt.colorValue || opt.color || '#ccc' } : {}}
                                  title={opt.name || opt.label || opt.value || `Option ${optIdx + 1}`}
                                >
                                  {displayType === 'swatches' && (opt.image_url || opt.imageUrl) && (
                                    <img src={opt.image_url || opt.imageUrl} alt="" className="w-full h-full object-cover" />
                                  )}
                                </div>
                              );
                            }
                            // Cards - show small thumbnails
                            if (displayType === 'cards_scroll' || displayType === 'cards_rich' || displayType === 'tabs_grid') {
                              return (
                                <div
                                  key={optIdx}
                                  className={`w-8 h-8 rounded border overflow-hidden ${optIdx === 0 ? 'border-blue-500' : 'border-gray-200'}`}
                                >
                                  {opt.image_url || opt.imageUrl ? (
                                    <img src={opt.image_url || opt.imageUrl} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                      <ImageIcon className="h-3 w-3 text-gray-400" />
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            // Default: text buttons
                            return (
                              <span
                                key={optIdx}
                                className={`px-2 py-0.5 text-xs rounded border ${optIdx === 0 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                              >
                                {opt.name || opt.label || opt.value || `Option ${optIdx + 1}`}
                              </span>
                            );
                          })}
                          {variant.options?.length > 5 && (
                            <span className="text-xs text-gray-400">+{variant.options.length - 5}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-center py-4 text-gray-500">
                <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No variant options configured yet</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => setIsOpen(true)}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Configure Variants
                </Button>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Modal with WYSIWYG Editor */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-blue-600" />
                  {cardConfig.title || `Manage ${listingTypeLabel} Variants`}
                </DialogTitle>
                <DialogDescription>
                  Edit variants directly as they appear on your storefront
                </DialogDescription>
              </div>
              {totalCombinations > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBulkEdit(!showBulkEdit)}
                  className="hidden sm:flex"
                >
                  Bulk Edit Prices
                </Button>
              )}
            </div>
          </DialogHeader>
          
          {/* Mobile Tabbed View (visible on small screens) */}
          <div className="md:hidden">
            <Tabs value={mobileTab} onValueChange={setMobileTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mx-4 mt-4" style={{ width: 'calc(100% - 32px)' }}>
                <TabsTrigger value="editor" className="flex items-center gap-1">
                  <PenSquare className="h-4 w-4" />
                  Editor
                </TabsTrigger>
                <TabsTrigger value="preview" className="flex items-center gap-1">
                  <Eye className="h-4 w-4" />
                  Preview
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="editor" className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 280px)' }}>
                {/* Quick Add Buttons - Show only when no variants */}
                {variants.length === 0 && (
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-sm text-blue-800 mb-2">Quick add a variant type:</p>
                    <div className="flex flex-wrap gap-2">
                      {commonVariants.map((name) => (
                        <Button
                          key={name}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleQuickAddVariant(name)}
                          className="bg-white"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          {name}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                <VariantManagerV2
                  variants={variants}
                  variantCombinations={variantCombinations}
                  onVariantsChange={(v) => handleChange('variants', v)}
                  onCombinationsChange={(c) => handleChange('variantCombinations', c)}
                  basePrice={parseFloat(
                    // iter-362 — same type-aware base-price waterfall as
                    // SchemaCardRenderer's (now dead, USE_MODAL_EDITORS=true)
                    // inline path. This modal is the ACTUAL active UI, and it
                    // was missing the Event/Rental branches, so `formData.price`
                    // (empty for events — price lives under `ticketPrice`)
                    // resolved to 0, losing the base price the moment variants
                    // were added.
                    schema?.listingType === 'Subscriptions' || schema?.listingType === 'subscription'
                      ? (formData.base_plan_price || formData.unit_price || formData.price)
                      : schema?.listingType === 'event'
                        ? (formData.ticketPrice || formData.price)
                        : schema?.listingType === 'rental'
                          ? (formData.dailyRate || formData.price)
                          : formData.price
                  ) || 0}
                  listingType={listingType}
                  config={cardConfig.config}
                  hideHeader={true}
                  hideAddButton={variants.length === 0}
                />
              </TabsContent>
              
              <TabsContent value="preview" className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 280px)' }}>
                {variants.length > 0 ? (
                  <Card className="bg-white shadow-sm">
                    <CardContent className="p-4 space-y-4">
                      {variants.map((variant, idx) => {
                        const displayType = variant.display_config || variant.displayType || 'buttons';
                        return (
                          <div key={variant.id || idx} className="space-y-2">
                            <Label className="text-sm font-medium">{variant.name || 'Unnamed Variant'}</Label>
                            
                            {/* Text Buttons (default) */}
                            {(displayType === 'buttons' || !displayType) && variant.options?.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {variant.options?.map((opt, optIdx) => (
                                  <span
                                    key={optIdx}
                                    className={`px-3 py-1.5 text-sm rounded-lg border-2 ${optIdx === 0 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                                  >
                                    {opt.name || opt.label || opt.value || `Option ${optIdx + 1}`}
                                  </span>
                                ))}
                              </div>
                            )}
                            
                            {/* Color Swatches */}
                            {displayType === 'color_swatch' && (
                              <div className="flex flex-wrap gap-2">
                                {variant.options?.map((opt, optIdx) => (
                                  <div
                                    key={optIdx}
                                    className={`w-8 h-8 rounded-full border-2 ${optIdx === 0 ? 'border-blue-600 ring-2 ring-blue-200' : 'border-gray-200'}`}
                                    style={{ backgroundColor: opt.colorValue || opt.color || '#ccc' }}
                                    title={opt.name || opt.label || opt.value || `Option ${optIdx + 1}`}
                                  />
                                ))}
                              </div>
                            )}
                            
                            {/* Image Swatches */}
                            {displayType === 'swatches' && (
                              <div className="flex flex-wrap gap-2">
                                {variant.options?.map((opt, optIdx) => (
                                  <div
                                    key={optIdx}
                                    className={`w-12 h-12 rounded-lg overflow-hidden border-2 ${optIdx === 0 ? 'border-blue-600 ring-2 ring-blue-200' : 'border-gray-200'}`}
                                  >
                                    {opt.image_url || opt.imageUrl ? (
                                      <img src={opt.image_url || opt.imageUrl} alt={opt.name} className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                        <ImageIcon className="h-4 w-4 text-gray-400" />
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Scrollable & Rich Cards - simplified for mobile */}
                            {(displayType === 'cards_scroll' || displayType === 'cards_rich') && (
                              <div className="flex gap-2 overflow-x-auto">
                                {variant.options?.map((opt, optIdx) => (
                                  <div
                                    key={optIdx}
                                    className={`flex-shrink-0 w-20 rounded-lg overflow-hidden border-2 ${optIdx === 0 ? 'border-blue-600' : 'border-gray-200'}`}
                                  >
                                    <div className="aspect-square bg-gray-100 flex items-center justify-center">
                                      {opt.image_url || opt.imageUrl ? (
                                        <img src={opt.image_url || opt.imageUrl} alt={opt.name} className="w-full h-full object-cover" />
                                      ) : (
                                        <ImageIcon className="h-6 w-6 text-gray-400" />
                                      )}
                                    </div>
                                    <p className="text-xs text-center p-1 truncate">{opt.name || opt.label}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Tabs Grid - simplified */}
                            {displayType === 'tabs_grid' && (
                              <div className="border rounded-lg overflow-hidden">
                                <div className="flex bg-gray-100 overflow-x-auto">
                                  {variant.options?.map((opt, optIdx) => (
                                    <button
                                      key={optIdx}
                                      className={`px-3 py-1.5 text-xs whitespace-nowrap ${optIdx === 0 ? 'bg-white text-blue-600' : 'text-gray-600'}`}
                                    >
                                      {opt.name || opt.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {totalCombinations > 0 && (
                        <div className="pt-4 border-t">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Variant Combinations</span>
                            <span className="font-medium">{totalCombinations} total</span>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="bg-white shadow-sm">
                    <CardContent className="p-8 text-center text-gray-400">
                      <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Add variants to see preview</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </div>
          
          {/* Desktop Side-by-Side View (hidden on small screens) */}
          <div className="hidden md:flex flex-1 overflow-hidden" style={{ maxHeight: 'calc(90vh - 180px)' }}>
            {/* Left Panel - Variant Editor (60% width) */}
            <div className="w-[60%] overflow-y-auto p-6">
              {/* Pricing Mode Toggle */}
              {formData.vendorHasManualPricing && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border mb-4">
                  <div>
                    <p className="text-sm font-medium">Pricing Mode</p>
                    <p className="text-xs text-gray-500">
                      {formData.manualPricingForProduct 
                        ? 'Manual: Final price confirmed offline' 
                        : 'Automatic: Prices update based on selections'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${!formData.manualPricingForProduct ? 'font-medium text-green-600' : 'text-gray-400'}`}>
                      Auto
                    </span>
                    <button
                      type="button"
                      onClick={() => handleChange('manualPricingForProduct', !formData.manualPricingForProduct)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        formData.manualPricingForProduct ? 'bg-amber-500' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData.manualPricingForProduct ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className={`text-xs ${formData.manualPricingForProduct ? 'font-medium text-amber-600' : 'text-gray-400'}`}>
                      Manual
                    </span>
                  </div>
                </div>
              )}

              {/* Quick Add Buttons - Show only when no variants */}
              {variants.length === 0 && (
                <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-sm text-blue-800 mb-2">Quick add a variant type:</p>
                  <div className="flex flex-wrap gap-2">
                    {commonVariants.map((name) => (
                      <Button
                        key={name}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickAddVariant(name)}
                        className="bg-white"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        {name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <VariantManagerV2
                variants={variants}
                variantCombinations={variantCombinations}
                onVariantsChange={(v) => handleChange('variants', v)}
                onCombinationsChange={(c) => handleChange('variantCombinations', c)}
                basePrice={parseFloat(
                  // iter-362 — see mobile-tab VariantManagerV2 above for rationale.
                  schema?.listingType === 'Subscriptions' || schema?.listingType === 'subscription'
                    ? (formData.base_plan_price || formData.unit_price || formData.price)
                    : schema?.listingType === 'event'
                      ? (formData.ticketPrice || formData.price)
                      : schema?.listingType === 'rental'
                        ? (formData.dailyRate || formData.price)
                        : formData.price
                ) || 0}
                listingType={listingType}
                config={cardConfig.config}
                hideHeader={true}
                hideAddButton={variants.length === 0}
              />
            </div>
            
            {/* Right Panel - Live Preview (40% width) */}
            <div className="w-[40%] bg-slate-50 overflow-y-auto border-l">
              {/* Preview Header */}
              <div className="sticky top-0 z-10 bg-slate-100 border-b px-6 py-3">
                <div className="flex items-center gap-2">
                  <Eye className="h-5 w-5 text-slate-600" />
                  <span className="font-semibold text-slate-800">Live Preview</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">How customers will see variant options</p>
              </div>
              
              <div className="p-6">
                {variants.length > 0 ? (
                  <>
                    {/* Product Preview Card */}
                    <Card className="bg-white shadow-md border-0">
                      {/* Mock Product Header */}
                      <div className="p-4 border-b bg-gradient-to-r from-slate-50 to-white">
                        <div className="flex items-start gap-3">
                          <div className="w-16 h-16 bg-slate-200 rounded-lg flex items-center justify-center">
                            <Package className="h-8 w-8 text-slate-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-800">{formData.title || 'Your Product'}</h3>
                            <p className="text-lg font-bold text-blue-600">₹{parseFloat(formData.price || 0).toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                      
                      <CardContent className="p-4 space-y-5">
                        {variants.map((variant, idx) => {
                          // Get the display type - stored as display_config (string) in VariantManagerV2
                          const displayType = variant.display_config || variant.displayType || 'buttons';
                          
                          // Get display type label for badge
                          const getDisplayTypeLabel = (type) => {
                            switch(type) {
                              case 'buttons': return 'Text Buttons';
                              case 'swatches': return 'Image Swatches';
                              case 'cards_scroll': return 'Scrollable Cards';
                              case 'cards_rich': return 'Rich Cards';
                              case 'tabs_grid': return 'Tabs with Grid';
                              case 'color_swatch': return 'Color Swatches';
                              default: return 'Buttons';
                            }
                          };
                          
                          return (
                            <div key={variant.id || idx} className="space-y-3">
                              {/* Variant Header */}
                              <div className="flex items-center justify-between">
                                <Label className="text-base font-semibold text-slate-800">
                                  {variant.name || 'Unnamed Variant'}
                                  <span className="text-red-500 ml-1">*</span>
                                </Label>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600">
                                    {getDisplayTypeLabel(displayType)}
                                  </Badge>
                                  {variant.options?.length > 0 && (
                                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600">
                                      {variant.options.length} options
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              
                              {/* ============ TEXT BUTTONS (default) ============ */}
                              {(displayType === 'buttons' || !displayType) && variant.options?.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {variant.options?.map((opt, optIdx) => {
                                    const priceImpact = parseFloat(opt.price_impact || opt.priceImpact || 0);
                                    return (
                                      <button
                                        key={optIdx}
                                        type="button"
                                        className={`px-4 py-2 text-sm rounded-xl border-2 transition-all font-medium ${
                                          optIdx === 0 
                                            ? 'bg-blue-600 text-white border-blue-600 shadow-md' 
                                            : 'bg-white text-slate-700 border-slate-200 hover:border-blue-400 hover:bg-blue-50'
                                        }`}
                                      >
                                        {opt.name || opt.label || opt.value || `Option ${optIdx + 1}`}
                                        {priceImpact > 0 && (
                                          <span className={`ml-1 ${optIdx === 0 ? 'text-blue-100' : 'text-green-600'}`}>
                                            +₹{priceImpact.toLocaleString()}
                                          </span>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                              
                              {/* ============ COLOR SWATCHES (legacy) ============ */}
                              {displayType === 'color_swatch' && (
                                <div className="flex flex-wrap gap-3">
                                  {variant.options?.map((opt, optIdx) => (
                                    <button
                                      key={optIdx}
                                      type="button"
                                      className={`w-10 h-10 rounded-full border-2 transition-all shadow-sm ${
                                        optIdx === 0 
                                          ? 'border-blue-600 ring-2 ring-blue-200 ring-offset-2' 
                                          : 'border-slate-200 hover:border-slate-400'
                                      }`}
                                      style={{ backgroundColor: opt.colorValue || opt.color || '#e2e8f0' }}
                                      title={opt.name || opt.label || opt.value || `Color ${optIdx + 1}`}
                                    />
                                  ))}
                                </div>
                              )}
                              
                              {/* ============ IMAGE SWATCHES ============ */}
                              {displayType === 'swatches' && (
                                <div className="flex flex-wrap gap-3">
                                  {variant.options?.map((opt, optIdx) => (
                                    <div
                                      key={optIdx}
                                      className={`relative cursor-pointer rounded-lg overflow-hidden transition-all ${
                                        optIdx === 0 
                                          ? 'ring-2 ring-blue-500 ring-offset-2 shadow-lg' 
                                          : 'ring-1 ring-slate-200 hover:ring-blue-300 hover:shadow-md'
                                      }`}
                                    >
                                      {optIdx === 0 && (
                                        <div className="absolute top-1 right-1 z-10 bg-blue-600 rounded-full p-0.5">
                                          <Check className="h-3 w-3 text-white" />
                                        </div>
                                      )}
                                      <div className="w-14 h-14 bg-slate-100 flex items-center justify-center">
                                        {opt.image_url || opt.imageUrl ? (
                                          <img 
                                            src={opt.image_url || opt.imageUrl} 
                                            alt={opt.name || opt.label} 
                                            className="w-full h-full object-cover" 
                                          />
                                        ) : opt.colorValue || opt.color ? (
                                          <div 
                                            className="w-full h-full" 
                                            style={{ backgroundColor: opt.colorValue || opt.color }}
                                          />
                                        ) : (
                                          <ImageIcon className="h-6 w-6 text-slate-400" />
                                        )}
                                      </div>
                                      <div className="text-center py-1 px-1 bg-white">
                                        <p className="text-xs font-medium truncate">{opt.name || opt.label || `Option ${optIdx + 1}`}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {/* ============ SCROLLABLE CARDS ============ */}
                              {displayType === 'cards_scroll' && (
                                <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
                                  {variant.options?.map((opt, optIdx) => {
                                    const priceImpact = parseFloat(opt.price_impact || opt.priceImpact || 0);
                                    return (
                                      <div
                                        key={optIdx}
                                        className={`flex-shrink-0 w-28 cursor-pointer rounded-xl overflow-hidden transition-all border-2 ${
                                          optIdx === 0 
                                            ? 'border-blue-500 shadow-lg' 
                                            : 'border-slate-200 hover:border-blue-300 hover:shadow-md'
                                        }`}
                                      >
                                        {optIdx === 0 && (
                                          <div className="absolute top-2 right-2 z-10 bg-blue-600 rounded-full p-1">
                                            <Check className="h-3 w-3 text-white" />
                                          </div>
                                        )}
                                        <div className="aspect-square bg-slate-100 relative">
                                          {opt.image_url || opt.imageUrl ? (
                                            <img 
                                              src={opt.image_url || opt.imageUrl} 
                                              alt={opt.name || opt.label} 
                                              className="w-full h-full object-cover" 
                                            />
                                          ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                              <ImageIcon className="h-10 w-10 text-slate-300" />
                                            </div>
                                          )}
                                        </div>
                                        <div className="p-2 bg-white text-center">
                                          <p className="text-sm font-medium text-slate-800 truncate">
                                            {opt.name || opt.label || `Option ${optIdx + 1}`}
                                          </p>
                                          {priceImpact > 0 && (
                                            <p className="text-xs text-green-600 font-semibold">+₹{priceImpact.toLocaleString()}</p>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              
                              {/* ============ RICH CARDS (70/30) ============ */}
                              {displayType === 'cards_rich' && (
                                <div className="grid grid-cols-2 gap-3">
                                  {variant.options?.map((opt, optIdx) => {
                                    const priceImpact = parseFloat(opt.price_impact || opt.priceImpact || 0);
                                    return (
                                      <div
                                        key={optIdx}
                                        className={`cursor-pointer rounded-xl overflow-hidden transition-all border-2 ${
                                          optIdx === 0 
                                            ? 'border-blue-500 shadow-lg' 
                                            : 'border-slate-200 hover:border-blue-300 hover:shadow-md'
                                        }`}
                                      >
                                        {/* 70% Image */}
                                        <div className="relative" style={{ paddingBottom: '70%' }}>
                                          {optIdx === 0 && (
                                            <div className="absolute top-2 right-2 z-10 bg-blue-600 rounded-full p-1">
                                              <Check className="h-4 w-4 text-white" />
                                            </div>
                                          )}
                                          <div className="absolute inset-0 bg-slate-100">
                                            {opt.image_url || opt.imageUrl ? (
                                              <img 
                                                src={opt.image_url || opt.imageUrl} 
                                                alt={opt.name || opt.label} 
                                                className="w-full h-full object-cover" 
                                              />
                                            ) : (
                                              <div className="w-full h-full flex items-center justify-center">
                                                <ImageIcon className="h-12 w-12 text-slate-300" />
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        {/* 30% Text */}
                                        <div className="p-3 bg-white">
                                          <p className="font-semibold text-slate-800">
                                            {opt.name || opt.label || `Option ${optIdx + 1}`}
                                          </p>
                                          {opt.description && (
                                            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{opt.description}</p>
                                          )}
                                          {priceImpact > 0 && (
                                            <p className="text-sm text-green-600 font-semibold mt-1">+₹{priceImpact.toLocaleString()}</p>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              
                              {/* ============ TABS WITH GRID ============ */}
                              {displayType === 'tabs_grid' && variant.options?.length > 0 && (
                                <div className="border rounded-xl overflow-hidden">
                                  {/* Tab Headers */}
                                  <div className="flex bg-slate-100 border-b overflow-x-auto">
                                    {variant.options?.map((opt, optIdx) => (
                                      <button
                                        key={optIdx}
                                        type="button"
                                        className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                                          optIdx === 0 
                                            ? 'bg-white text-blue-600 border-b-2 border-blue-600' 
                                            : 'text-slate-600 hover:bg-slate-50'
                                        }`}
                                      >
                                        {opt.name || opt.label || `Option ${optIdx + 1}`}
                                      </button>
                                    ))}
                                  </div>
                                  {/* Tab Content - Image Grid */}
                                  <div className="p-4 bg-white">
                                    <div className="grid grid-cols-3 gap-2">
                                      {[1, 2, 3, 4, 5, 6].map((i) => (
                                        <div key={i.id || i.name || `${i.title || "i"}-${i}`} className="aspect-square bg-slate-100 rounded-lg flex items-center justify-center">
                                          {variant.options?.[0]?.image_url || variant.options?.[0]?.imageUrl ? (
                                            <img 
                                              src={variant.options[0].image_url || variant.options[0].imageUrl} 
                                              alt={`Gallery ${i}`}
                                              className="w-full h-full object-cover rounded-lg"
                                            />
                                          ) : (
                                            <ImageIcon className="h-6 w-6 text-slate-300" />
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                    {variant.options?.[0]?.description && (
                                      <p className="text-sm text-slate-600 mt-3">{variant.options[0].description}</p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        
                        {/* Combinations Summary */}
                        {totalCombinations > 0 && (
                          <div className="pt-5 border-t mt-5 bg-slate-50 -mx-4 px-4 py-4 rounded-b-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 text-slate-600">
                                <Package className="h-5 w-5" />
                                <span className="text-sm font-medium">Total Combinations</span>
                              </div>
                              <span className="font-bold text-lg text-slate-800">{totalCombinations}</span>
                            </div>
                            
                            {/* Price Range */}
                            {variantCombinations?.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-slate-200">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-slate-500">Price Range</span>
                                  <span className="font-semibold text-blue-600">
                                    ₹{Math.min(...variantCombinations.map(c => parseFloat(c.price) || 0)).toLocaleString()}
                                    {' - '}
                                    ₹{Math.max(...variantCombinations.map(c => parseFloat(c.price) || 0)).toLocaleString()}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between text-sm mt-1">
                                  <span className="text-slate-500">Total Stock</span>
                                  <span className="font-semibold text-slate-800">
                                    {variantCombinations.reduce((sum, c) => sum + (parseInt(c.inventory || c.stock) || 0), 0)} units
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    
                    {/* Add to Cart Button Mock */}
                    <Button className="w-full mt-4 h-12 text-lg font-semibold" disabled>
                      Select Options to Add to Cart
                    </Button>
                  </>
                ) : (
                  <Card className="bg-white shadow-md border-0">
                    <CardContent className="p-8 text-center">
                      <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                        <Package className="h-8 w-8 text-slate-400 opacity-50" />
                      </div>
                      <p className="font-medium text-slate-600 mb-1">No variants configured</p>
                      <p className="text-sm text-slate-400">Add variants like Size, Color to see how they&apos;ll appear to customers</p>
                    </CardContent>
                  </Card>
                )}
              </div>
              
              {/* Bulk Edit Panel */}
              {showBulkEdit && totalCombinations > 0 && (
                <Card className="mt-4 bg-white shadow-sm border-amber-200">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">Bulk Edit Prices</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-xs">Set all prices to:</Label>
                      <div className="flex gap-2 mt-1">
                        <Input 
                          type="number" 
                          placeholder="₹0" 
                          className="h-8"
                          id="bulk-price"
                        />
                        <Button 
                          size="sm" 
                          className="h-8"
                          onClick={() => {
                            const val = document.getElementById('bulk-price').value;
                            if (val) handleBulkPriceUpdate(parseFloat(val), 'fixed');
                          }}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Adjust all prices by:</Label>
                      <div className="flex gap-2 mt-1">
                        <Input 
                          type="number" 
                          placeholder="+/-₹0" 
                          className="h-8"
                          id="bulk-adjust"
                        />
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-8"
                          onClick={() => {
                            const val = document.getElementById('bulk-adjust').value;
                            if (val) handleBulkPriceUpdate(parseFloat(val), 'adjust');
                          }}
                        >
                          Adjust
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
          
          <DialogFooter className="px-6 py-4 border-t bg-gray-50">
            <Button type="button" variant="outline" onClick={() => setIsOpen(false)} disabled={isSaving}>
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} data-testid="save-variants-btn" disabled={isSaving}>
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-1" />
              )}
              {isSaving ? 'Saving...' : 'Save Variants'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VariantEditorModal;
